import {
  Box,
  Button,
  ButtonGroup,
  Flex,
  FormLabel,
  HStack,
  Text,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { FormProvider } from "react-hook-form";

import { Restricted } from "@/app/components/Restricted";
import FormReset from "@/common/components/Form/FormReset";
import { FormRootError } from "@/common/components/Form/FormRootError";
import FormSelect from "@/common/components/Form/FormSelect";
import FormSubmit from "@/common/components/Form/FormSubmit";
import { useEnvironment } from "@/common/contexts/EnvironmentContext";
import { commonQueryKeys } from "@/common/data/commonQueryKeys";
import useApiForm from "@/common/hooks/useApiForm";
import { useFeedbackPromptStatistics } from "@/common/hooks/useFeedbackPromptStatistics";
import api from "@/common/utils/api";
import { normalizeSlackChannel } from "@/common/utils/slack";
import FeedbackPromptStatisticsChart from "@/feedback/components/FeedbackPromptStatisticsChart";
import FeedbackPromptStatisticsTable from "@/feedback/components/FeedbackPromptStatisticsTable";
import { SlackChannelAutocompleteSelect } from "@/integration/components/slack/SlackChannelAutocompleteSelect";
import { integrationQueryKeys } from "@/integration/data/integrationQueryKeys";
import {
  EnvironmentPatchArgs,
  EnvironmentPatchSchema,
} from "@reflag/shared/environmentAPI";
import { SlackChannel } from "@reflag/shared/slackConnectionAPI";
import {
  PatchSlackNotificationSettings,
  SlackNotificationSettings,
} from "@reflag/shared/targetingNotificationPolicyAPI";

import FeedbackPromptingInstalledWrapper from "./FeedbackPromptingInstalledWrapper";

const STATS_START = dayjs.utc().subtract(6, "days").startOf("day").toDate();
const STATS_END = dayjs.utc().add(1, "day").startOf("day").toDate();

const MIN = 60;
const HOUR = 60 * MIN;
const DAY = 24 * HOUR;
const WEEK = 7 * DAY;
const MONTH = 30 * DAY;

function channelsMatch(a: SlackChannel | null, b: SlackChannel | null) {
  return (a?.id ?? null) === (b?.id ?? null);
}

export default function FeedbackPrompting() {
  const { env, appId, orgId, org } = useEnvironment();

  const toast = useToast();
  const queryClient = useQueryClient();
  const [isSavingFeedbackSlackChannel, setIsSavingFeedbackSlackChannel] =
    useState(false);

  const slackNotificationSettingsQueryKey =
    integrationQueryKeys.appSlackNotificationSettings(appId);
  const slackNotificationSettingsQuery = useQuery({
    queryKey: slackNotificationSettingsQueryKey,
    queryFn: () =>
      api
        .get<"/apps/:appId/notification-settings/slack">(
          `/apps/${appId}/notification-settings/slack`,
        )
        .then((res) => res.data),
    refetchOnWindowFocus: false,
  });

  const savedFeedbackSlackChannel = normalizeSlackChannel(
    slackNotificationSettingsQuery.data?.feedbackSlackChannel,
  );
  const [feedbackSlackChannel, setFeedbackSlackChannel] =
    useState<SlackChannel | null>(savedFeedbackSlackChannel ?? null);

  useEffect(() => {
    setFeedbackSlackChannel(savedFeedbackSlackChannel ?? null);
  }, [savedFeedbackSlackChannel]);

  const { form, handleSubmit } = useApiForm(
    (data: EnvironmentPatchArgs) =>
      api
        .patch<"/apps/:appId/environments/:envId">(
          `/apps/${appId}/environments/${env.id}`,
          data,
        )
        .then((res) => res.data),
    EnvironmentPatchSchema,
    {
      onSuccess: (data) => {
        toast({
          title: "Settings saved",
          status: "success",
        });

        queryClient.invalidateQueries({
          queryKey: commonQueryKeys.bootstrap(orgId),
        });
        form.reset({
          feedbackPromptSnoozePeriodSeconds:
            data.autoFeedbackSurveys.snoozePeriodSeconds,
        });
      },
    },
    {
      defaultValues: {
        feedbackPromptSnoozePeriodSeconds:
          env.autoFeedbackSurveys.snoozePeriodSeconds,
      },
    },
  );

  const promptStats = useFeedbackPromptStatistics(
    undefined,
    STATS_START,
    STATS_END,
  );

  const handleSaveFeedbackSlackChannel = async () => {
    setIsSavingFeedbackSlackChannel(true);

    try {
      const payload: PatchSlackNotificationSettings = {
        feedbackSlackChannel,
      };
      const { data } =
        await api.patch<"/apps/:appId/notification-settings/slack">(
          `/apps/${appId}/notification-settings/slack`,
          payload,
        );

      queryClient.setQueryData<SlackNotificationSettings>(
        slackNotificationSettingsQueryKey,
        data,
      );
      await queryClient.invalidateQueries({
        queryKey: slackNotificationSettingsQueryKey,
      });

      toast({
        title: "Feedback Slack channel saved",
        status: "success",
      });
    } catch {
      toast({
        title: "Failed to save feedback Slack channel",
        status: "error",
      });
    } finally {
      setIsSavingFeedbackSlackChannel(false);
    }
  };

  return (
    <Flex direction="column" gap={4} maxW="mediumForm">
      <Restricted alertStyle="callout" role="admin">
        <FeedbackPromptingInstalledWrapper>
          <VStack align="flex-start" spacing={5}>
            <VStack align="flex-start" spacing={2}>
              <FormLabel>Global prompting statistics</FormLabel>
              <HStack spacing={12}>
                <FeedbackPromptStatisticsTable {...promptStats} />
                <Box minW="180px">
                  <FeedbackPromptStatisticsChart {...promptStats} />
                </Box>
              </HStack>
            </VStack>

            <VStack align="flex-start" spacing={2} w="full">
              <FormLabel m={0}>Feedback notification Slack channel</FormLabel>
              <Text color="dimmed" fontSize="sm" maxW="2xl">
                Feedback notifications use this channel by default. Leave it
                empty to fall back to the Slack channel set in Notification
                policies.
              </Text>
              <SlackChannelAutocompleteSelect
                canClear
                isDisabled={
                  !org.integrations.slack ||
                  isSavingFeedbackSlackChannel ||
                  slackNotificationSettingsQuery.isLoading
                }
                mb={2}
                maxW="sm"
                value={feedbackSlackChannel}
                onChange={(channel) => setFeedbackSlackChannel(channel)}
              />
              <Button
                isDisabled={
                  !org.integrations.slack ||
                  slackNotificationSettingsQuery.isLoading ||
                  channelsMatch(
                    feedbackSlackChannel,
                    savedFeedbackSlackChannel ?? null,
                  )
                }
                isLoading={isSavingFeedbackSlackChannel}
                size="sm"
                onClick={() => void handleSaveFeedbackSlackChannel()}
              >
                Save feedback channel
              </Button>
            </VStack>

            <form onSubmit={handleSubmit}>
              <FormProvider {...form}>
                <VStack align="flex-start" maxW="compactForm" spacing={4}>
                  <FormSelect
                    description="The user will not be asked for feedback again until this time has passed."
                    label="Minimum time between asking for feedback"
                    name="feedbackPromptSnoozePeriodSeconds"
                    options={[
                      { value: 5 * MIN, label: "5 minutes" },
                      { value: 20 * MIN, label: "20 minutes" },
                      { value: 1 * HOUR, label: "1 hour" },
                      { value: 6 * HOUR, label: "6 hours" },
                      { value: 2 * DAY, label: "2 days" },
                      { value: 5 * DAY, label: "5 days" },
                      { value: 1 * WEEK, label: "1 week" },
                      { value: 2 * WEEK, label: "2 weeks" },
                      { value: 1 * MONTH, label: "1 month" },
                      { value: 2 * MONTH, label: "2 months" },
                      { value: 3 * MONTH, label: "3 months" },
                    ]}
                    valueAsNumber
                  />

                  <FormRootError />

                  <ButtonGroup>
                    <FormSubmit showPostSubmit />
                    <FormReset />
                  </ButtonGroup>
                </VStack>
              </FormProvider>
            </form>
          </VStack>
        </FeedbackPromptingInstalledWrapper>
      </Restricted>
    </Flex>
  );
}
